import React, { useState, useEffect, useCallback } from "react";
import { AuthenticationManager } from "managers";
import { AuthenticationContext } from "contexts";
import { PASSWORD_EXPIRED_EVENT } from "utils";

export function AuthenticationProvider({ children }) {
  const [user, setUser] = useState(undefined);
  // Cuando el login requiere segundo factor, guardamos aquí el pendingToken para que
  // App/Login muestren la pantalla de verificación 2FA en vez de iniciar sesión.
  const [pending2FA, setPending2FA] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      AuthenticationManager.refreshUsuarioLogin()
        .then((data) => {
          setCurrentSession(data);
        })
        .catch((error) => {
          setCurrentSession(null);
        });
    }, 500);
  }, []);

  const setCurrentSession = useCallback(
    (data) => {
      setUser(data?.usuarioLogin || null);
    },
    [user]
  );

  const refrescarSesion = useCallback(() => {
    return AuthenticationManager.refreshUsuarioLogin()
      .then((data) => setCurrentSession(data))
      .catch(() => setCurrentSession(null));
  }, [setCurrentSession]);

  // Si una petición devuelve PASSWORD_EXPIRED (sesión expira a mitad de uso),
  // recargamos la sesión para que App muestre la pantalla de cambio obligatorio.
  useEffect(() => {
    const handler = () => refrescarSesion();
    window.addEventListener(PASSWORD_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(PASSWORD_EXPIRED_EVENT, handler);
  }, [refrescarSesion]);

  const login = (authModel) => {
    return AuthenticationManager.login(authModel).then((data) => {
      if (data?.requiere2FA) {
        setPending2FA({ pendingToken: data.pendingToken });
      } else {
        setCurrentSession(data);
      }
    });
  };

  const verify2fa = (codigo, confiarDispositivo) => {
    return AuthenticationManager.verify2fa({
      PendingToken: pending2FA?.pendingToken,
      Codigo: codigo,
      ConfiarDispositivo: !!confiarDispositivo,
    }).then((data) => {
      setPending2FA(null);
      setCurrentSession(data);
    });
  };

  const resend2fa = () => {
    return AuthenticationManager.resend2fa({
      PendingToken: pending2FA?.pendingToken,
    }).then((data) => {
      if (data?.pendingToken) {
        setPending2FA({ pendingToken: data.pendingToken });
      }
    });
  };

  const cancelar2FA = () => setPending2FA(null);

  function logout() {
    return AuthenticationManager.logout().then(() => {
      setCurrentSession(null);
    });
  }

  const cambiarCompania = (model) => {
    return AuthenticationManager.cambiarCompania(model).then((data) => {
      setCurrentSession(data);
    });
  };

  const volverCompaniaOriginal = () => {
    return AuthenticationManager.volverCompaniaOriginal().then((data) => {
      setCurrentSession(data);
    });
  };

  return (
    <AuthenticationContext.Provider
      value={{ user, login, logout, cambiarCompania, volverCompaniaOriginal, refrescarSesion, pending2FA, verify2fa, resend2fa, cancelar2FA }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}
